import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { useCalendlyPopup } from '../../hooks/useCalendlyPopup';
import { Breadcrumb } from '../Breadcrumb';
import { PhoneModal } from '../PhoneModal';

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
  50% { box-shadow: 0 0 30px rgba(255, 215, 0, 0.5); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

// Premium Header
const Header = styled.header<{ $scrolled: boolean }>`
  background: ${props => props.$scrolled 
    ? 'linear-gradient(135deg, rgba(26, 35, 50, 0.98) 0%, rgba(44, 62, 80, 0.98) 100%)' 
    : 'linear-gradient(135deg, #1a2332 0%, #2c3e50 100%)'};
  padding: ${props => props.$scrolled ? '10px 0' : '12px 0'};
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 9999;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(20px);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-bottom: 2px solid rgba(255, 215, 0, 0.2);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.5), transparent);
    animation: ${shimmer} 10s infinite linear;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${props => props.$scrolled ? '6px 0' : '8px 0'};
  }
`;

const HeaderContainer = styled.div<{ $scrolled: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 280px 1fr 280px;
  align-items: center;
  gap: 30px;
  height: auto;
  transition: all 0.4s;
  
  @media (max-width: 1400px) {
    grid-template-columns: 250px 1fr 200px;
    padding: 0 30px;
  }
  
  @media (max-width: 1250px) {
    display: flex;
    justify-content: space-between;
    padding: 0 20px;
    height: ${props => props.$scrolled ? '38px' : '42px'};
  }
`;

const LogoWrapper = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const LogoImg = styled.img`
  height: 36px;
  width: auto;
  filter: drop-shadow(0 4px 10px rgba(255, 215, 0, 0.3));
  
  @media (min-width: 1400px) {
    height: 40px;
  }
  
  @media (max-width: 1200px) {
    height: 32px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 28px;
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  
  .brand {
    font-size: 20px;
    font-weight: 900;
    color: white;
    letter-spacing: -0.5px;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
  
  .tagline {
    font-size: 14px;
    color: #ffd700;
    font-weight: 700;
    font-style: italic;
    letter-spacing: 1.2px;
    opacity: 0.95;
    text-transform: uppercase;
  }
  
  @media (min-width: 1400px) {
    .tagline {
      font-size: 15px;
      letter-spacing: 1.5px;
    }
  }
  
  @media (max-width: 1200px) {
    .brand {
      font-size: 18px;
    }
    .tagline {
      font-size: 12px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

const MobileTagline = styled.div`
  display: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: block;
    font-size: 11px;
    color: #ffd700;
    font-weight: 700;
    font-style: italic;
    letter-spacing: 0.8px;
    opacity: 0.95;
    text-transform: uppercase;
    margin-left: 8px;
    white-space: nowrap;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 1250px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 20px;
  border-radius: 30px;
  transition: all 0.3s;
  display: inline-block;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #ffd700, #ffa500);
    transition: all 0.3s;
    transform: translateX(-50%);
  }
  
  &:hover {
    color: #ffd700;
    background: rgba(255, 215, 0, 0.1);
    transform: translateY(-2px);
    
    &::before {
      width: 80%;
    }
  }
  
  @media (max-width: 1200px) {
    font-size: 14px;
    padding: 10px 16px;
  }
`;

const DropdownButton = styled.button`
  color: rgba(255, 255, 255, 0.95);
  background: transparent;
  border: none;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 20px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #ffd700, #ffa500);
    transition: all 0.3s;
    transform: translateX(-50%);
  }
  
  &:hover, &.open {
    color: #ffd700;
    background: rgba(255, 215, 0, 0.1);
    transform: translateY(-2px);
    
    &::before {
      width: 80%;
    }
  }
  
  .arrow {
    font-size: 10px;
    transition: transform 0.3s;
  }
  
  &.open .arrow {
    transform: rotate(180deg);
  }
  
  @media (max-width: 1200px) {
    font-size: 14px;
    padding: 10px 16px;
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #2c3e50 0%, #1a2332 100%);
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: 20px;
  padding: 15px;
  min-width: 250px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(-10px);
  transition: all 0.3s;
  z-index: 1000;
  
  &.open {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid rgba(255, 215, 0, 0.3);
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 14px 18px;
  margin-bottom: 10px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%);
    border-color: rgba(255, 215, 0, 0.5);
    color: #ffd700;
    transform: translateX(5px);
  }
`;

const HeaderCTA = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  justify-content: flex-end;
  
  @media (max-width: 1400px) {
    gap: 10px;
  }
  
  @media (max-width: 1250px) {
    display: none;
  }
`;

const PhoneButton = styled.button`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.08) 100%);
  color: #ffd700;
  padding: 6px 14px;
  border-radius: 5px;
  border: 1px solid rgba(255, 215, 0, 0.3);
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.25) 0%, rgba(255, 215, 0, 0.15) 100%);
    border-color: #ffd700;
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(255, 215, 0, 0.3);
  }
  
  .icon {
    font-size: 14px;
  }
  
  .phone-number {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    gap: 0;
  }
  
  @media (max-width: 1200px) {
    font-size: 11px;
  }
`;

const ScheduleButton = styled.button`
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  color: #1a2332;
  padding: 6px 14px;
  border: none;
  border-radius: 5px;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s;
  box-shadow: 0 3px 15px rgba(255, 215, 0, 0.2);
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(255, 215, 0, 0.4);
    
    &::before {
      width: 200px;
      height: 200px;
    }
  }
  
  .schedule-text {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .schedule-icon {
    font-size: 14px;
    display: none;
    
    @media (max-width: 768px) {
      display: inline;
    }
  }
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    min-width: auto;
  }
  
  @media (max-width: 1200px) {
    font-size: 11px;
    padding: 6px 12px;
  }
`;

// Mobile Menu
const MobileMenuButton = styled.button`
  display: none;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%);
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: 12px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: rgba(255, 215, 0, 0.2);
    border-color: #ffd700;
  }
  
  @media (max-width: 1250px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const MenuText = styled.span`
  color: #ffd700;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const FooterMenuButton = styled.button`
  display: flex;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%);
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: 12px;
  cursor: pointer;
  padding: 10px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s;
  
  &:hover {
    background: rgba(255, 215, 0, 0.2);
    border-color: #ffd700;
  }
`;

const Hamburger = styled.div<{ $isOpen: boolean }>`
  width: 24px;
  height: 20px;
  position: relative;
  
  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: #ffd700;
    transition: all 0.3s;
    
    &:nth-child(1) {
      top: ${props => props.$isOpen ? '50%' : '0'};
      transform: ${props => props.$isOpen ? 'translateY(-50%) rotate(45deg)' : 'none'};
    }
    
    &:nth-child(2) {
      top: 50%;
      transform: translateY(-50%);
      opacity: ${props => props.$isOpen ? 0 : 1};
    }
    
    &:nth-child(3) {
      bottom: ${props => props.$isOpen ? '50%' : '0'};
      transform: ${props => props.$isOpen ? 'translateY(50%) rotate(-45deg)' : 'none'};
    }
  }
`;

const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  z-index: 9998;
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100vh;
  height: 100dvh;
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 100%);
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 9999;
  overflow-y: auto;
  padding: 0;
  padding-bottom: env(safe-area-inset-bottom, 20px);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const MobileNavLink = styled(Link)<{ $isActive?: boolean }>`
  display: block;
  color: ${props => props.$isActive ? '#ffd700' : 'white'};
  text-decoration: none;
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  padding: 8px 15px;
  margin-bottom: 1px;
  border-radius: 0;
  background: ${props => props.$isActive 
    ? 'rgba(255, 215, 0, 0.08)' 
    : 'transparent'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-left: ${props => props.$isActive ? '3px solid #ffd700' : '3px solid transparent'};
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  
  &:hover {
    background: ${props => props.$isActive 
      ? 'rgba(255, 215, 0, 0.12)' 
      : 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%)'};
    border-color: rgba(255, 215, 0, 0.5);
    color: #ffd700;
    transform: translateX(${props => props.$isActive ? '0' : '10px'});
  }
`;

const MobileScheduleButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  color: #1a2332;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 10px;
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
  transition: all 0.3s;
  
  &:active {
    transform: scale(0.98);
  }
`;

// Premium Footer
const Footer = styled.footer`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 80px 20px 30px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #ffd700, transparent);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at bottom, rgba(255, 215, 0, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FooterContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 50px;
  align-items: center;
  padding-bottom: 50px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 40px;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    height: 70px;
    width: auto;
    margin-bottom: 15px;
    filter: drop-shadow(0 5px 15px rgba(255, 215, 0, 0.3));
    animation: ${float} 4s ease-in-out infinite;
  }
  
  .tagline {
    color: #ffd700;
    font-size: 16px;
    font-weight: 600;
    font-style: italic;
    letter-spacing: 1px;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
`;

const FooterCenter = styled.div`
  text-align: center;
  
  h3 {
    font-size: 32px;
    font-weight: 900;
    color: white;
    margin-bottom: 20px;
    letter-spacing: -0.5px;
    
    .gold {
      color: #ffd700;
      text-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
    }
  }
  
  p {
    font-size: 18px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 30px;
  }
`;

const FooterStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 20px;
  }
`;

const StatItem = styled.div`
  text-align: center;
  
  .number {
    font-size: 28px;
    font-weight: 900;
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
  
  .label {
    font-size: 12px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 1px;
    margin-top: 5px;
  }
`;

const FooterBBB = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  a {
    display: block;
    transition: all 0.3s;
    
    &:hover {
      transform: scale(1.05);
    }
  }
  
  img {
    height: 80px;
    width: auto;
    filter: drop-shadow(0 5px 15px rgba(255, 215, 0, 0.3));
  }
  
  .rating {
    margin-top: 10px;
    font-size: 20px;
    font-weight: 800;
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
`;

const FooterBottom = styled.div`
  padding-top: 40px;
  text-align: center;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 15px;
  }
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: #ffd700;
    transition: width 0.3s;
  }
  
  &:hover {
    color: #ffd700;
    
    &::after {
      width: 100%;
    }
  }
`;

const Copyright = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  line-height: 1.6;
`;

const Disclaimer = styled.div`
  margin-top: 30px;
  padding: 25px;
  background: rgba(255, 215, 0, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 15px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: white;
  width: 100%;
  overflow-x: hidden;
`;

const MainContent = styled.main`
  padding-top: 60px;
  min-height: calc(100vh - 60px);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 1250px) {
    padding-top: 55px;
    padding-bottom: 70px;
  }
`;

const ContentWrapper = styled.div`
  padding-top: 0;
`;

const MobileFixedFooter = styled.div<{ $isVisible: boolean }>`
  display: none;
  
  @media (max-width: 1250px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(135deg, #1a2332 0%, #2c3e50 100%);
    border-top: 2px solid rgba(255, 215, 0, 0.3);
    z-index: 9997;
    align-items: center;
    justify-content: space-between;
    padding: 0 15px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
    transform: ${props => props.$isVisible ? 'translateY(0)' : 'translateY(100%)'};
    transition: transform 0.3s ease-in-out;
  }
`;


const TalkWithCapButton = styled.button`
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  color: #1a2332;
  padding: 10px 20px;
  border-radius: 0;
  border: 1px solid #1a2332;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 30px rgba(255, 215, 0, 0.5);
    
    &::before {
      width: 300px;
      height: 300px;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  .icon {
    font-size: 20px;
    animation: ${glow} 2s infinite ease-in-out;
  }
`;

interface SharedLayoutUltimateProps {
  children: React.ReactNode;
}

export const SharedLayoutUltimate: React.FC<SharedLayoutUltimateProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openCalendly } = useCalendlyPopup();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isLearnDropdownOpen, setIsLearnDropdownOpen] = useState(false);
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [showStickyFooter, setShowStickyFooter] = useState(true);
  const aboutDropdownRef = useRef<HTMLDivElement>(null);
  const toolsDropdownRef = useRef<HTMLDivElement>(null);
  const learnDropdownRef = useRef<HTMLDivElement>(null);
  const contactDropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Sticky footer is always visible now
      // setShowStickyFooter(window.scrollY > 600);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(event.target as Node)) {
        setIsAboutDropdownOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target as Node)) {
        setIsToolsDropdownOpen(false);
      }
      if (learnDropdownRef.current && !learnDropdownRef.current.contains(event.target as Node)) {
        setIsLearnDropdownOpen(false);
      }
      if (contactDropdownRef.current && !contactDropdownRef.current.contains(event.target as Node)) {
        setIsContactDropdownOpen(false);
      }
    };

    if (isAboutDropdownOpen || isToolsDropdownOpen || isLearnDropdownOpen || isContactDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isAboutDropdownOpen, isToolsDropdownOpen, isLearnDropdownOpen, isContactDropdownOpen]);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : '';
  };
  
  return (
    <PageWrapper>
      <Header $scrolled={scrolled}>
        <HeaderContainer $scrolled={scrolled}>
          <LogoWrapper to="/">
            <LogoContainer>
              <LogoImg src="/logo.svg" alt="Annuity Answers" />
              <LogoText>
                <span className="tagline">"Keep it real"</span>
              </LogoText>
              <MobileTagline>"Keep it real"</MobileTagline>
            </LogoContainer>
          </LogoWrapper>
          
          <Nav>
            <NavItem>
              <NavLink to="/">Home</NavLink>
            </NavItem>
            
            <div ref={aboutDropdownRef}>
              <NavItem className="dropdown-container">
                <DropdownButton
                  className={isAboutDropdownOpen ? 'open' : ''}
                  onClick={() => setIsAboutDropdownOpen(!isAboutDropdownOpen)}
                >
                  About
                  <span className="arrow">▼</span>
                </DropdownButton>
                <DropdownContent className={isAboutDropdownOpen ? 'open' : ''}>
                  <DropdownItem onClick={() => { navigate('/about-us'); setIsAboutDropdownOpen(false); }}>
                    About Us
                  </DropdownItem>
                  <DropdownItem onClick={() => { navigate('/testimonials'); setIsAboutDropdownOpen(false); }}>
                    Testimonials
                  </DropdownItem>
                </DropdownContent>
              </NavItem>
            </div>
            
            <div ref={learnDropdownRef}>
              <NavItem className="dropdown-container">
                <DropdownButton
                  className={isLearnDropdownOpen ? 'open' : ''}
                  onClick={() => setIsLearnDropdownOpen(!isLearnDropdownOpen)}
                >
                  Learn
                  <span className="arrow">▼</span>
                </DropdownButton>
                <DropdownContent className={isLearnDropdownOpen ? 'open' : ''}>
                  <DropdownItem onClick={() => { navigate('/annuity-basics'); setIsLearnDropdownOpen(false); }}>
                    Annuity Basics
                  </DropdownItem>
                  <DropdownItem onClick={() => { navigate('/averill-method'); setIsLearnDropdownOpen(false); }}>
                    The Averill Method
                  </DropdownItem>
                  <DropdownItem onClick={() => { navigate('/quintessential-question'); setIsLearnDropdownOpen(false); }}>
                    Quintessential Question
                  </DropdownItem>
                </DropdownContent>
              </NavItem>
            </div>
            
            <div ref={toolsDropdownRef}>
              <NavItem className="dropdown-container">
                <DropdownButton
                  className={isToolsDropdownOpen ? 'open' : ''}
                  onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                >
                  Tools
                  <span className="arrow">▼</span>
                </DropdownButton>
                <DropdownContent className={isToolsDropdownOpen ? 'open' : ''}>
                  <DropdownItem onClick={() => { navigate('/quick-answer'); setIsToolsDropdownOpen(false); }}>
                    Quick Answer
                  </DropdownItem>
                  <DropdownItem onClick={() => { navigate('/deep-dive'); setIsToolsDropdownOpen(false); }}>
                    Deep Dive
                  </DropdownItem>
                  <DropdownItem onClick={() => { navigate('/permutation-request'); setIsToolsDropdownOpen(false); }}>
                    Permutation Request
                  </DropdownItem>
                </DropdownContent>
              </NavItem>
            </div>
            
            <div ref={contactDropdownRef}>
              <NavItem className="dropdown-container">
                <DropdownButton
                  className={isContactDropdownOpen ? 'open' : ''}
                  onClick={() => setIsContactDropdownOpen(!isContactDropdownOpen)}
                >
                  Contact
                  <span className="arrow">▼</span>
                </DropdownButton>
                <DropdownContent className={isContactDropdownOpen ? 'open' : ''}>
                  <DropdownItem onClick={() => { navigate('/contact'); setIsContactDropdownOpen(false); }}>
                    Contact Us
                  </DropdownItem>
                  <div style={{ 
                    borderTop: '2px solid rgba(255, 215, 0, 0.2)', 
                    marginTop: '10px', 
                    paddingTop: '10px' 
                  }}>
                    <div style={{ 
                      color: '#ffd700', 
                      fontSize: '11px', 
                      fontWeight: '700', 
                      padding: '8px 18px 5px 18px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      opacity: 0.9
                    }}>
                      Free Consultation
                    </div>
                    <DropdownItem 
                      onClick={() => { navigate('/schedule'); setIsContactDropdownOpen(false); }}
                      style={{ 
                        paddingLeft: '30px',
                        fontSize: '13px',
                        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(255, 215, 0, 0.03) 100%)'
                      }}
                    >
                      For Annuity Owners
                    </DropdownItem>
                    <DropdownItem 
                      onClick={() => { navigate('/schedule'); setIsContactDropdownOpen(false); }}
                      style={{ 
                        paddingLeft: '30px',
                        fontSize: '13px',
                        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(255, 215, 0, 0.03) 100%)'
                      }}
                    >
                      For Advisors
                    </DropdownItem>
                  </div>
                </DropdownContent>
              </NavItem>
            </div>
          </Nav>
          
          <HeaderCTA>
            <PhoneButton 
              onClick={() => navigate('/contact')}
              style={{
                background: 'transparent',
                border: '1px solid white',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                padding: '4px 12px'
              }}
            >
              <span style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '0.5px' }}>CALL NOW</span>
              <span style={{ fontSize: '13px', fontWeight: '700' }}>800-922-1894</span>
            </PhoneButton>
            <TalkWithCapButton 
              onClick={() => setIsPhoneModalOpen(true)}
              style={{ 
                padding: '6px 16px',
                fontSize: '12px',
                borderRadius: '5px',
                height: 'auto',
                minWidth: '160px',
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <img 
                src="/Cap/Cap.png" 
                alt="Cap"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '9px', fontWeight: '600', letterSpacing: '0.5px', opacity: 0.9 }}>OR CONNECT DIRECTLY</span>
                <span style={{ fontSize: '13px', fontWeight: '700' }}>TALK WITH CAP</span>
              </div>
            </TalkWithCapButton>
          </HeaderCTA>
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            <Hamburger $isOpen={isMobileMenuOpen}>
              <span></span>
              <span></span>
              <span></span>
            </Hamburger>
            <MenuText>MENU</MenuText>
          </MobileMenuButton>
        </HeaderContainer>
      </Header>
      
      <MobileMenuOverlay $isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} />
      <MobileMenu $isOpen={isMobileMenuOpen}>
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'rgba(255, 215, 0, 0.1)',
          borderBottom: '2px solid rgba(255, 215, 0, 0.3)',
          padding: '8px 15px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10
        }}>
          <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>Menu</span>
          <button
            onClick={toggleMobileMenu}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 215, 0, 0.5)',
              color: '#ffd700',
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
            aria-label="Close menu"
          >
            ✕ CLOSE
          </button>
        </div>
        <div style={{
          height: 'calc(100% - 50px)',
          overflowY: 'auto',
          paddingBottom: '80px'
        }}>
          <MobileNavLink to="/" onClick={toggleMobileMenu} $isActive={location.pathname === '/'}>Home</MobileNavLink>
          <MobileNavLink to="/about-us" onClick={toggleMobileMenu} $isActive={location.pathname === '/about-us'}>About Us</MobileNavLink>
          <MobileNavLink to="/testimonials" onClick={toggleMobileMenu} $isActive={location.pathname === '/testimonials'}>Testimonials</MobileNavLink>
          <MobileNavLink to="/annuity-basics" onClick={toggleMobileMenu} $isActive={location.pathname === '/annuity-basics'}>Annuity Basics</MobileNavLink>
          <MobileNavLink to="/averill-method" onClick={toggleMobileMenu} $isActive={location.pathname === '/averill-method'}>The Averill Method</MobileNavLink>
          <MobileNavLink to="/quintessential-question" onClick={toggleMobileMenu} $isActive={location.pathname === '/quintessential-question'}>Quintessential Question</MobileNavLink>
          <MobileNavLink to="/quick-answer" onClick={toggleMobileMenu} $isActive={location.pathname === '/quick-answer'}>Quick Answer</MobileNavLink>
          <MobileNavLink to="/deep-dive" onClick={toggleMobileMenu} $isActive={location.pathname === '/deep-dive'}>Deep Dive</MobileNavLink>
          <MobileNavLink to="/permutation-request" onClick={toggleMobileMenu} $isActive={location.pathname === '/permutation-request'}>Permutation Request</MobileNavLink>
          <MobileNavLink to="/contact" onClick={toggleMobileMenu} $isActive={location.pathname === '/contact'}>Contact Us</MobileNavLink>
          <div style={{ 
            borderTop: '1px solid rgba(255, 215, 0, 0.2)', 
            marginTop: '10px', 
            marginBottom: '10px',
            paddingTop: '10px' 
          }}>
            <div style={{ 
              color: '#ffd700', 
              fontSize: '11px', 
              fontWeight: '700', 
              padding: '5px 15px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Free Consultation
            </div>
            <MobileNavLink 
              to="/schedule" 
              onClick={toggleMobileMenu}
              $isActive={location.pathname === '/schedule'}
              style={{ 
                paddingLeft: '30px',
                fontSize: '13px',
                background: location.pathname === '/schedule' ? undefined : 'rgba(255, 215, 0, 0.05)'
              }}
            >
              For Annuity Owners
            </MobileNavLink>
            <MobileNavLink 
              to="/schedule" 
              onClick={toggleMobileMenu}
              $isActive={location.pathname === '/schedule'}
              style={{ 
                paddingLeft: '30px',
                fontSize: '13px',
                background: location.pathname === '/schedule' ? undefined : 'rgba(255, 215, 0, 0.05)'
              }}
            >
              For Advisors
            </MobileNavLink>
          </div>
          <button
            onClick={() => window.location.href = 'tel:800-922-1894'}
            style={{
              background: 'transparent',
              color: 'white',
              border: '1px solid white',
              padding: '14px 20px',
              margin: '15px 20px 10px 20px',
              width: 'calc(100% - 40px)',
              borderRadius: '0',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              transition: 'all 0.3s',
              textAlign: 'center'
            }}
          >
            CALL NOW: 800-922-1894
          </button>
          <button
            onClick={() => { setIsPhoneModalOpen(true); toggleMobileMenu(); }}
            style={{
              background: '#2c3e50',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '14px 20px',
              margin: '5px 20px 60px 20px',
              width: 'calc(100% - 40px)',
              borderRadius: '0',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              transition: 'all 0.3s',
              textAlign: 'center',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
          >
            <img 
              src="/Cap/Cap.png" 
              alt="Cap Averill"
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                objectFit: 'cover',
                objectPosition: 'center top',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            />
            <span>CONNECT WITH CAP</span>
          </button>
        </div>
      </MobileMenu>
      
      <Breadcrumb />
      <MainContent>
        <ContentWrapper>
          {children}
        </ContentWrapper>
      </MainContent>
      
      <MobileFixedFooter $isVisible={showStickyFooter}>
        <TalkWithCapButton onClick={() => setIsPhoneModalOpen(true)}>
          <img 
            src="/Cap/Cap.png" 
            alt="Cap"
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              objectFit: 'cover',
              objectPosition: 'center top',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          />
          <span>TALK WITH CAP</span>
        </TalkWithCapButton>
        <FooterMenuButton onClick={toggleMobileMenu}>
          <Hamburger $isOpen={isMobileMenuOpen}>
            <span></span>
            <span></span>
            <span></span>
          </Hamburger>
          <MenuText>MENU</MenuText>
        </FooterMenuButton>
      </MobileFixedFooter>
      
      <Footer>
        <FooterContainer>
          <FooterTop>
            <FooterLogo>
              <img src="/logo.svg" alt="Cap Averill II & Associates" />
              <span className="tagline">"Keep it real"</span>
            </FooterLogo>
            
            <FooterCenter>
              <h3>Securing <span className="gold">Your Future</span> for 40 Years</h3>
              <p>
                Annuityanswers.com is run by Cap Averill II & Associates.
              </p>
              <FooterStats>
                <StatItem>
                  <div className="number">40</div>
                  <div className="label">Years Experience</div>
                </StatItem>
                <StatItem>
                  <div className="number">10K+</div>
                  <div className="label">Happy Clients</div>
                </StatItem>
                <StatItem>
                  <div className="number">Zero</div>
                  <div className="label">Complaints</div>
                </StatItem>
                <StatItem>
                  <div className="number">100%</div>
                  <div className="label">Free Service</div>
                </StatItem>
              </FooterStats>
            </FooterCenter>
            
            <FooterBBB>
              <a 
                href="https://www.bbb.org/us/oh/toledo/profile/retirement-planning-services/cap-averill-ii-associates-inc-0422-13000009"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/bbb.png" alt="BBB Accredited Business" />
              </a>
              <div className="rating">A+ RATING</div>
            </FooterBBB>
          </FooterTop>
          
          <FooterBottom>
            <FooterLinks>
              <FooterLink to="/privacy-policy">Privacy</FooterLink>
              <FooterLink to="/terms">Terms</FooterLink>
              <FooterLink to="/disclosures">Disclosures</FooterLink>
              <FooterLink to="/about-us">About</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
            </FooterLinks>
            
            <Copyright>
              © {new Date().getFullYear()} Cap Averill II & Associates. All Rights Reserved.
              <br />
              <em>"Happy clients tell the story"</em>
            </Copyright>
            
            <Disclaimer>
              This website is not intended as investment advice or a recommendation to purchase any specific product. 
              Always consult with qualified financial and legal advisors before making investment decisions.
            </Disclaimer>
          </FooterBottom>
        </FooterContainer>
      </Footer>
      
      <PhoneModal 
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        openCalendly={openCalendly}
      />
    </PageWrapper>
  );
};